// 📁 src/pages/FinalDashboard.jsx
import React, { useEffect, useState } from "react";
import axios from "../utils/axiosInstance";
import { useAuth } from "../context/AuthContext";
import { useIdea } from "../context/IdeaContext";
import { useNavigate } from "react-router-dom";

const FinalDashboard = () => {
  const { user } = useAuth();
  const { selectedIdea } = useIdea();
  const navigate = useNavigate();

  const [summary, setSummary] = useState(null);
  const [aiInsights, setAiInsights] = useState([]);
  const [loading, setLoading] = useState(true);

  // 🆕 All Startups vs Selected Startup
  const [viewMode, setViewMode] = useState("all");

  useEffect(() => {
    const fetchDashboard = async () => {
      setLoading(true);
      try {
        const params = {};
        if (viewMode === "current" && selectedIdea?.ideaId) {
          params.ideaId = selectedIdea.ideaId;
        }

        const res = await axios.get("/api/dashboard/summary", { params });
        setSummary(res?.data?.summary || null);
        // 🔥 Fetch GPT insights
      try {
        const insightRes = await axios.post("/api/dashboard/insights", {
          summary: res?.data?.summary,
        });

        setAiInsights(insightRes.data.insights || []);
      } catch (err) {
        console.error("GPT insights failed:", err);
      }
      } catch (err) {
        console.error("❌ Dashboard error:", err);
        setSummary(null);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, [viewMode, selectedIdea?.ideaId]);

  if (loading) return <p className="p-6">Loading dashboard...</p>;
  if (!summary) return <p className="p-6 text-red-600">No data found.</p>;

  // ⏳ Summary calculations (normalized)
    const ideaCount = summary.idea?.count || 0;

    const frameworksUsed = summary.research?.count || 0;

    const mvpSteps = summary.mvp?.stepCount || 0;

    const progress = summary.progress || 0;

    // 🧠 Startup Health Score (basic v1)
const healthScore =
  (summary.idea?.count > 0 ? 15 : 0) +
  (summary.research?.count > 0 ? 15 : 0) +
  (summary.mvp?.stepCount > 0 ? 20 : 0) +
  (summary.businessModel ? 15 : 0) +
  (summary.pitchDeck ? 10 : 0) +
  (summary.gtm ? 10 : 0) +
  (summary.aiTesting ? 10 : 0) +
  (summary.funding ? 5 : 0);

  // 🧠 AI Insights Generator (v1 rules-based)
    const insights = [];

    if (summary.idea?.count > 0 && summary.research?.count === 0) {
      insights.push("You have an idea but no market validation yet.");
    }

    if (summary.research?.count > 0 && summary.mvp?.stepCount === 0) {
      insights.push("Research done. Next step: Build your MVP.");
    }

    if (summary.mvp?.stepCount > 0 && !summary.gtm) {
      insights.push("You have an MVP plan but no go-to-market strategy.");
    }

    if (!summary.funding && summary.pitchDeck) {
      insights.push("Pitch deck ready. Start exploring funding opportunities.");
    }

    if (healthScore >= 80) {
      insights.push("Your startup is investor-ready. Consider pitching.");
    }

    if (insights.length === 0) {
      insights.push("You're just getting started. Begin with idea generation.");
    }

  const currentStartupLabel =
    selectedIdea?.ideaTitle || selectedIdea?.title || "Current Startup";

    // 🧠 Status helper
    const getStatus = (condition) => {
      if (condition === true) return "✅ Completed";
      if (condition === false) return "❌ Not Started";
      return "⚠️ Partial";
    };

  // 🌟 Enhanced dashboard cards list
  const cards = [
    {
      title: "Idea Generation",
      subtitle: `${getStatus(ideaCount > 0)} • ${ideaCount} idea${ideaCount !== 1 ? "s" : ""} generated`,
      link: "/idea-generation",
      color: "bg-blue-600",
    },
    {
      title: "Market Research",
      subtitle: `${getStatus(frameworksUsed > 0)} • ${frameworksUsed} framework${frameworksUsed !== 1 ? "s" : ""} analyzed`,
      link: "/market-research",
      color: "bg-indigo-600",
    },
    {
      title: "MVP Planner",
      subtitle: `${getStatus(mvpSteps > 0)} • ${mvpSteps} steps defined`,
      link: "/mvp-planner",
      color: "bg-green-600",
    },
    {
      title: "MVP Agent",
      subtitle: `${getStatus(summary.mvp?.hasAgent)} • ${
      summary.mvp?.hasAgent ? "Task breakdown ready" : "Not used yet"
      }`,
      link: "/mvp-agent",
      color: "bg-emerald-600",
    },
    {
      title: "Business Model",
      subtitle: `${getStatus(summary.businessModel)} • ${
      summary.businessModel ? "Draft saved" : "Not started"
      }`,
      link: "/business-model-builder",
      color: "bg-purple-600",
    },
    {
      title: "Pitch Deck",
      subtitle: `${getStatus(summary.pitchDeck)} • ${
      summary.pitchDeck ? "Deck generated" : "Not generated"
      }`,
      link: "/pitchdeck",
      color: "bg-pink-600",
    },
    {
      title: "GTM Accelerator",
      subtitle: `${getStatus(summary.gtm)} • ${
      summary.gtm ? "Strategy ready" : "Pending"
      }`,
      link: "/gtm-accelerator",
      color: "bg-red-600",
    },
    {
      title: "AI Testing",
      subtitle: `${getStatus(summary.aiTesting)} • ${
      summary.aiTesting ? "Insights available" : "Not tested yet"
      }`,
      link: "/ai-testing",
      color: "bg-cyan-600",
    },
    {
      title: "Funding Match",
      subtitle: `${getStatus(summary.funding)} • ${
      summary.funding ? "Matches found" : "Pending"
      }`,
      link: "/funding-match",
      color: "bg-yellow-600",
    },
  ];

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header + View switch */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
  <h1 className="text-3xl font-bold text-gray-800">
    🚀 Your Startup Journey Dashboard
  </h1>

  {selectedIdea && (
    <p className="text-sm text-gray-600 mt-1">
      Startup: <span className="font-semibold">
        {selectedIdea.ideaTitle || selectedIdea.title}
      </span>
    </p>
  )}
</div>

        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-600">View:</span>
          <select
            className="border rounded px-2 py-1 text-sm"
            value={viewMode}
            onChange={(e) => setViewMode(e.target.value)}
          >
            <option value="all">All Startups</option>
            <option value="current" disabled={!selectedIdea?.ideaId}>
              {selectedIdea?.ideaId
                ? `This Startup: ${currentStartupLabel}`
                : "Current Startup (select idea first)"}
            </option>
          </select>
        </div>
      </div>

      {/* Progress + Health Score */}
      <div className="mb-8">

        <div className="flex justify-between text-sm mb-1">
          <span>Overall Progress</span>
          <span>{progress}%</span>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-4">
          <div
            className="bg-blue-600 h-4 rounded-full transition-all duration-500"
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        {/* 🧠 Health Score */}
        <div className="mt-4 flex justify-between items-center">
          <span className="text-sm text-gray-600">Startup Health Score</span>
          <span className="text-lg font-bold text-green-600">
            {healthScore} / 100
          </span>
        </div>

        {viewMode === "current" && selectedIdea?.ideaId && (
          <p className="mt-1 text-xs text-gray-500">
            Scoped to startup: <strong>{currentStartupLabel}</strong>
          </p>
        )}
      </div>

      {/* Cards section */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cards.map((card, idx) => (
          <div
            key={idx}
            onClick={() => navigate(card.link)}
            className={`${card.color} text-white p-6 rounded-xl shadow hover:shadow-xl transition cursor-pointer`}
          >
            <h3 className="text-xl font-bold mb-2">{card.title}</h3>
            <p className="text-sm opacity-90">{card.subtitle}</p>
            <p className="mt-3 text-white/90 text-xs underline">Resume →</p>
          </div>
        ))}
      </div>

        {/* 🧠 AI Strategic Insights */}
<div className="mt-10 bg-white rounded-xl shadow p-6">
  <h2 className="text-xl font-semibold mb-4 text-gray-800">
    AI Strategic Insights
  </h2>

  {/* Strengths Block (visual only) */}
  <div className="mb-4 border-l-4 border-blue-500 bg-gray-50 p-4 rounded">
    <h3 className="font-semibold mb-2">Strengths</h3>
    <ul className="list-disc pl-5">
      {aiInsights.slice(0, 2).map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  </div>

  {/* Risks Block (visual only) */}
  <div className="mb-4 border-l-4 border-red-500 bg-red-50 p-4 rounded">
    <h3 className="font-semibold mb-2">Risks</h3>
    <ul className="list-disc pl-5">
      {aiInsights.slice(2, 3).map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  </div>

  {/* Recommendations Block (visual only) */}
  <div className="border-l-4 border-green-500 bg-green-50 p-4 rounded">
    <h3 className="font-semibold mb-2">Recommendations</h3>
    <ul className="list-disc pl-5">
      {aiInsights.slice(3).map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  </div>
</div>

      {/* Last Idea Snapshot */}
      {summary.idea?.latest && (
        <div className="mt-10 bg-white rounded-xl shadow p-6">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">
            🧠 Last Idea Generated
          </h2>
          <p className="text-lg font-semibold text-blue-700">
            {summary.idea.latest?.title}
          </p>

          <p className="text-gray-700 mt-2">
            {summary.idea.latest?.description}
          </p>

          <p className="text-sm italic text-green-600 mt-2">
            {summary.idea.latest?.aiNote}
          </p>
        </div>
      )}

      {/* 💰 Funding Snapshot */}
{summary.funding && (
  <div className="mt-6 bg-white rounded-xl shadow p-6">
    <h2 className="text-xl font-semibold mb-4 text-gray-800">
      💰 Funding Snapshot
    </h2>

    <p className="text-gray-700">
      You have explored funding opportunities for this startup.
    </p>

    <p className="text-sm text-green-600 mt-2">
      Tip: Review investor matches and prepare outreach.
    </p>
  </div>
)}

      {/* 🧪 AI Testing Snapshot */}
{summary.aiTesting && (
  <div className="mt-6 bg-white rounded-xl shadow p-6">
    <h2 className="text-xl font-semibold mb-4 text-gray-800">
      🧪 AI Testing Snapshot
    </h2>

    <p className="text-gray-700">
      AI testing insights are available for your startup idea.
    </p>

    <p className="text-sm text-blue-600 mt-2">
      Tip: Use feedback to refine your MVP and positioning.
    </p>
  </div>
)}
    </div>
  );
};

export default FinalDashboard;
